<script setup lang="ts">
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
// import chickenCard from "@/assets/images/home/chickensmall.png";
import chickenCard from "@/assets/images/home/chickensmall_recolored_da920b.png";
import eggCard from "@/assets/images/home/egg.png";
import { useDefaultStore } from "@/store";
// import eggCard from "@/assets/images/home/jaje_transparentna_pozadina.png";

definePage({
	name: "HomePage",
});

const categories = [
	{ icon: "🎬", label: "Movies", delay: "0s", to: "/movies", disabled: false },
	{ icon: "🎵", label: "Music", delay: "0.8s", to: "/music", disabled: false },
	{
		icon: "👤",
		label: "People",
		delay: "1.6s",
		to: "/people",
		disabled: false,
	},
];

type TWhatsNewItem = {
	date: string;
	description: string;
	id: string | number;
	title: string;
};

const store = useDefaultStore();
const isWhatsNewVisible = ref(false);
const isWhatsNewLoading = ref(false);
const whatsNewItems = ref<TWhatsNewItem[]>([]);
const hasLoadedWhatsNew = ref(false);

const shouldShowWhatsNew = computed(() =>
	normalizeBoolean(store.userData?.usr_should_show_whats_new),
);

function handleCategoryClick(event: MouseEvent, disabled: boolean) {
	if (disabled) {
		event.preventDefault();
	}
}

function normalizeBoolean(raw: unknown): boolean {
	if (typeof raw === "boolean") return raw;
	if (typeof raw === "number") return raw === 1;
	if (typeof raw === "string") return raw === "1" || raw === "true";

	return false;
}

function getFirstArray(...values: any[]): any[] {
	for (const value of values) {
		if (Array.isArray(value)) {
			return value;
		}
	}

	return [];
}

function normalizeWhatsNewItem(raw: any, index: number): TWhatsNewItem {
	if (typeof raw === "string") {
		return {
			date: "",
			id: index,
			title: raw,
			description: "",
		};
	}

	return {
		date: formatWhatsNewDate(
			raw?.date ?? raw?.created_at ?? raw?.utc_created ?? raw?.wnu_utc_created,
		),
		id: raw?.id ?? raw?.wnu_id ?? raw?.wn_id ?? raw?.key ?? index,
		title:
			String(
				raw?.title ??
					raw?.wnu_title ??
					raw?.wn_title ??
					raw?.heading ??
					raw?.name ??
					"",
			).trim() || "Update",
		description: String(
			raw?.description ??
				raw?.wnu_description ??
				raw?.wn_description ??
				raw?.text ??
				raw?.content ??
				"",
		).trim(),
	};
}

function formatWhatsNewDate(raw: unknown) {
	if (!raw) {
		return "";
	}

	const date = new Date(String(raw).replace(" ", "T"));

	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return new Intl.DateTimeFormat(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
	}).format(date);
}

function getWhatsNewItemsFromResponse(payload: any) {
	const root =
		payload?.data?.data ?? payload?.data?.res ?? payload?.data ?? payload;

	return getFirstArray(
		root?.items,
		root?.whatsNew,
		root?.whats_new,
		root?.updates,
		root?.data,
		root,
	).map(normalizeWhatsNewItem);
}

async function openWhatsNew() {
	isWhatsNewVisible.value = true;

	if (hasLoadedWhatsNew.value || isWhatsNewLoading.value) {
		return;
	}

	try {
		isWhatsNewLoading.value = true;
		const res = await api.getWhatsNew({
			token: store.userData?.usr_token ?? null,
		});

		whatsNewItems.value = getWhatsNewItemsFromResponse(res);
		hasLoadedWhatsNew.value = true;
	} catch (err: any) {
		console.warn(err.message);
		whatsNewItems.value = [];
		hasLoadedWhatsNew.value = true;
	} finally {
		isWhatsNewLoading.value = false;
	}
}

useHead({
	title: getPageTitle(),
	meta: [
		{
			name: "title",
			content: getPageTitle(),
		},
		{
			name: "description",
			content:
				"Play quick comparison games where you guess what came first across movies and more.",
		},
		{ name: "robots", content: "index, follow" },
		// defined in index api
		// {
		// 	property: "og:title",
		// 	content: getPageTitle(),
		// },
		// {
		// 	property: "og:description",
		// 	content:
		// 		"Play quick comparison games where you guess what came first across movies and more.",
		// },
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>
<template>
	<section class="home-page">
		<div class="overlay"></div>

		<div class="viewport-shell">
			<button
				v-if="shouldShowWhatsNew"
				type="button"
				class="whats-new-button"
				@click="openWhatsNew"
			>
				What's new
			</button>

			<div class="content-area">
				<div class="content">
					<div
						class="content-top-wrapper"
						:aria-label="APP_NAME"
					>
						<img
							:src="chickenCard"
							class="brand-image brand-image--left"
							alt=""
							aria-hidden="true"
						/>
						<div class="brand-wordmark">
							<span>The</span>
							<span>Older</span>
							<span>one</span>
						</div>
						<img
							:src="eggCard"
							class="brand-image brand-image--right"
							alt=""
							aria-hidden="true"
						/>
					</div>

					<p class="eyebrow">Choose a category</p>

					<div class="icon-row">
						<RouterLink
							v-for="item in categories"
							:key="item.label"
							:to="item.to"
							class="icon-card"
							:class="{ 'is-disabled': item.disabled }"
							:style="{ animationDelay: item.delay }"
							:aria-disabled="item.disabled"
							:tabindex="item.disabled ? -1 : 0"
							@click="handleCategoryClick($event, item.disabled)"
						>
							<span class="icon">{{ item.icon }}</span>
							<span class="label">{{ item.label }}</span>
						</RouterLink>
					</div>
				</div>
			</div>

			<div class="bottom-meta">
				<div class="bottom-meta__left">
					Powered by
					<a
						href="https://marsengine.net/"
						target="_blank"
						rel="noopener noreferrer"
						class="mars-engine-link"
					>
						MARS Engine
					</a>
				</div>

				<div class="bottom-meta__right">
					<RouterLink :to="{ name: 'PrivacyPolicyPage' }">
						Privacy Policy
					</RouterLink>
					<RouterLink :to="{ name: 'CookiePolicyPage' }">
						Cookie Policy
					</RouterLink>
					<RouterLink :to="{ name: 'TermsOfUsePage' }">Terms of Use</RouterLink>
				</div>
			</div>

			<!-- <div class="floating floating-1">🎬</div>
			<div class="floating floating-2">🎵</div>
			<div class="floating floating-3">👤</div>
			<div class="floating floating-4">🎞️</div>
			<div class="floating floating-5">🎧</div> -->
		</div>

		<Dialog
			v-model:visible="isWhatsNewVisible"
			modal
			class="whats-new-modal"
			header="What's new"
			:style="{ width: 'min(520px, calc(100vw - 32px))' }"
			:pt="{
				root: { class: 'whats-new-modal' },
				mask: { class: 'whats-new-modal-mask' },
				header: { class: 'whats-new-modal__header' },
				title: { class: 'whats-new-modal__title' },
				content: { class: 'whats-new-modal__content' },
				pcCloseButton: {
					root: { class: 'whats-new-modal__close' },
				},
			}"
		>
			<div
				v-if="isWhatsNewLoading"
				class="whats-new-state"
			>
				Loading updates...
			</div>

			<div
				v-else-if="!whatsNewItems.length"
				class="whats-new-state"
			>
				No updates yet.
			</div>

			<ul
				v-else
				class="whats-new-list"
			>
				<li
					v-for="item in whatsNewItems"
					:key="item.id"
					class="whats-new-item"
				>
					<strong>{{ item.title }}</strong>
					<span
						v-if="item.date"
						class="whats-new-date"
					>
						{{ item.date }}
					</span>
					<p v-if="item.description">{{ item.description }}</p>
				</li>
			</ul>
		</Dialog>
	</section>
</template>
<style lang="scss" scoped>
.home-page {
	position: relative;
	min-height: 100svh;
	overflow: hidden;
	background: radial-gradient(
			circle at center,
			#2a2a2a 0%,
			#171717 46%,
			#070707 100%
		),
		#101010;
	// Color variant
	// background:
	// 	linear-gradient(135deg, #15110d 0%, #22190f 42%, #121820 100%),
	// 	#141414;
	// background: linear-gradient(
	// 		180deg,
	// 		rgba(3, 4, 10, 0.62) 0%,
	// 		rgba(3, 4, 10, 0.28) 45%,
	// 		rgba(3, 4, 10, 0.7) 100%
	// 	),
	// 	url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
	);
	// Color variant
	// background: linear-gradient(
	// 		180deg,
	// 		rgba(255, 255, 255, 0.035) 0%,
	// 		rgba(6, 8, 12, 0.28) 48%,
	// 		rgba(2, 3, 6, 0.62) 100%
	// 	),
	// 	linear-gradient(
	// 		90deg,
	// 		rgba(218, 146, 11, 0.12) 0%,
	// 		rgba(255, 255, 255, 0) 34%,
	// 		rgba(91, 133, 166, 0.12) 100%
	// 	);
	z-index: 0;
}

.viewport-shell {
	position: relative;
	z-index: 2;
	width: 100%;
	min-height: 100dvh;
	display: flex;
	flex-direction: column;
	padding: 20px 20px calc(12px + env(safe-area-inset-bottom));
}

.whats-new-button {
	position: absolute;
	top: 20px;
	left: 20px;
	z-index: 4;
	border: 1px solid rgba(218, 146, 11, 0.46);
	border-radius: 999px;
	padding: 10px 14px 10px 30px;
	// background:
	// 	linear-gradient(135deg, rgba(218, 146, 11, 0.24), rgba(255, 255, 255, 0.1)),
	// 	rgba(255, 255, 255, 0.12);
	background: rgba(255, 255, 255, 0.12);
	color: $text-color-white;
	font-size: 13px;
	font-weight: 800;
	cursor: pointer;
	backdrop-filter: blur(10px);
	box-shadow:
		0 12px 30px rgba(0, 0, 0, 0.18),
		0 0 28px rgba(218, 146, 11, 0.22);
	animation: whats-new-pulse 2.4s ease-in-out infinite;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease;

	&::before {
		content: "";
		position: absolute;
		left: 13px;
		top: 50%;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #da920b;
		box-shadow: 0 0 0 6px rgba(218, 146, 11, 0.18);
		transform: translateY(-50%);
		animation: whats-new-dot 1.6s ease-in-out infinite;
	}

	&:hover {
		background: linear-gradient(
				135deg,
				rgba(218, 146, 11, 0.32),
				rgba(255, 255, 255, 0.14)
			),
			rgba(255, 255, 255, 0.16);
		border-color: rgba(218, 146, 11, 0.64);
		transform: translateY(-1px);
	}
}

.content-area {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 0;
}

.content {
	text-align: center;
	max-width: 900px;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.content-top-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: min(100%, 720px);
	min-height: 156px;
	margin-bottom: 14px;
}

.brand-image {
	display: block;
	width: clamp(110px, 36vw, 150px);
	height: auto;
	max-height: 150px;
	object-fit: contain;
	filter: drop-shadow(0 18px 24px rgba(0, 0, 0, 0.28));
	opacity: 0;
	transform-origin: center bottom;
	will-change: transform, opacity;
}

.brand-image--left {
	margin-right: -18px;
	animation: mobile-card-left 760ms cubic-bezier(0.2, 0.9, 0.2, 1.15) 120ms both;
}

.brand-image--right {
	margin-left: -18px;
	animation: mobile-card-right 760ms cubic-bezier(0.2, 0.9, 0.2, 1.15) 220ms
		both;
}

.brand-wordmark {
	display: none;
	align-items: baseline;
	justify-content: center;
	gap: 18px;
	max-width: 100%;
	margin: 0 22px;
	font-family:
		system-ui,
		-apple-system,
		BlinkMacSystemFont,
		"Segoe UI",
		sans-serif;
	font-size: clamp(58px, 8vw, 92px);
	font-weight: 950;
	line-height: 0.9;
	letter-spacing: 0;
	color: $text-color-white;
	text-shadow: 0 18px 38px rgba(0, 0, 0, 0.38);
	text-transform: lowercase;
	white-space: nowrap;
}

.brand-wordmark span {
	display: inline-block;
	opacity: 0;
	animation: wordmark-rise 720ms cubic-bezier(0.2, 0.85, 0.2, 1) both;
}

.brand-wordmark span:nth-child(2) {
	color: #da920b;
	animation-delay: 130ms;
}

.brand-wordmark span:nth-child(3) {
	animation-delay: 260ms;
}

.eyebrow {
	margin: 0;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 0.18em;
	text-transform: uppercase;
	color: $text-color-grey;
	opacity: 0;
	animation: category-content-rise 520ms cubic-bezier(0.2, 0.85, 0.2, 1) 760ms
		both;
}

.icon-row {
	margin-top: 28px;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 12px;
	opacity: 0;
	animation: category-content-rise 560ms cubic-bezier(0.2, 0.85, 0.2, 1) 900ms
		both;
}

.icon-card {
	display: inline-flex;
	align-items: center;
	gap: 12px;
	padding: 12px 18px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.18);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	text-decoration: none;
	cursor: pointer;
	transition: 0.2s ease;
}

.icon-card:not(.is-disabled):hover {
	background: rgba(255, 255, 255, 0.18);
}

.icon-card.is-disabled {
	opacity: 0.45;
	cursor: not-allowed;
	filter: grayscale(1);
	pointer-events: none;
}

.icon {
	font-size: 26px;
	line-height: 1;
}

.label {
	font-size: 14px;
	font-weight: 700;
	color: $text-color-white;
	letter-spacing: 0.02em;
}

.bottom-meta {
	margin-top: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 10px;
	padding-top: 12px;
}

.bottom-meta__left {
	font-size: 11px;
	font-weight: 600;
	color: rgba(255, 255, 255, 0.62);
	white-space: normal;

	.mars-engine-link {
		color: rgba(255, 255, 255, 0.62);
		text-decoration: none;

		&:hover {
			color: $text-color-white;
		}
	}
}

.bottom-meta__right {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 10px 14px;

	a {
		font-size: 11px;
		font-weight: 600;
		// color: rgba(255, 255, 255, 0.78);
		color: rgba(255, 255, 255, 0.62);
		text-decoration: none;
		transition: color 0.2s ease;

		&:hover {
			color: $text-color-white;
		}
	}
}

.whats-new-state {
	padding: 8px 0;
	color: rgba(255, 255, 255, 0.7);
	font-size: 14px;
	font-weight: 700;
}

.whats-new-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin: 0;
	padding: 0;
	list-style: none;
}

.whats-new-item {
	padding: 12px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.86);

	strong {
		display: block;
		color: $text-color-white;
		font-size: 15px;
		font-weight: 900;
	}

	.whats-new-date {
		display: block;
		margin-top: 4px;
		color: rgba(255, 255, 255, 0.5);
		font-size: 12px;
		font-weight: 700;
	}

	p {
		margin: 6px 0 0;
		color: rgba(255, 255, 255, 0.68);
		font-size: 13px;
		line-height: 1.45;
	}
}

:global(.whats-new-modal-mask) {
	background: rgba(0, 0, 0, 0.58);
	backdrop-filter: blur(4px);
}

:global(.whats-new-modal),
:global(.whats-new-modal.p-dialog) {
	border: 1px solid rgba(255, 255, 255, 0.14) !important;
	border-radius: 8px !important;
	background: radial-gradient(
			circle at center,
			rgba(42, 42, 42, 0.96),
			rgba(17, 17, 17, 0.98)
		),
		#111 !important;
	color: $text-color-white !important;
	box-shadow: 0 24px 80px rgba(0, 0, 0, 0.38) !important;
	overflow: hidden !important;
}

:global(.whats-new-modal__header),
:global(.whats-new-modal .p-dialog-header) {
	padding: 18px 18px 8px !important;
	background: transparent !important;
	border: 0 !important;
	color: $text-color-white !important;
}

:global(.whats-new-modal__title),
:global(.whats-new-modal .p-dialog-title) {
	color: $text-color-white !important;
	font-size: 20px !important;
	font-weight: 900 !important;
}

:global(.whats-new-modal__content),
:global(.whats-new-modal .p-dialog-content) {
	padding: 10px 18px 18px !important;
	background: transparent !important;
	color: $text-color-white !important;
}

:global(.whats-new-modal__close),
:global(.whats-new-modal .p-dialog-close-button) {
	width: 42px !important;
	height: 42px !important;
	border-radius: 50% !important;
	color: rgba(255, 255, 255, 0.76) !important;
	background: transparent !important;
}

.floating {
	position: absolute;
	z-index: 1;
	font-size: clamp(26px, 4vw, 42px);
	opacity: 0.18;
	filter: blur(0.2px);
	animation: drift 8s ease-in-out infinite;
	pointer-events: none;
}

.floating-1 {
	top: 14%;
	left: 12%;
	animation-delay: 0s;
}

.floating-2 {
	top: 22%;
	right: 14%;
	animation-delay: 1.2s;
}

.floating-3 {
	bottom: 18%;
	left: 16%;
	animation-delay: 2.1s;
}

.floating-4 {
	bottom: 14%;
	right: 18%;
	animation-delay: 0.7s;
}

.floating-5 {
	top: 50%;
	right: 8%;
	animation-delay: 1.8s;
}

@keyframes drift {
	0%,
	100% {
		transform: translate3d(0, 0, 0) rotate(0deg);
	}
	50% {
		transform: translate3d(0, -28px, 0) rotate(4deg);
	}
}

@keyframes wordmark-rise {
	0% {
		opacity: 0;
		transform: translateY(18px) scale(0.96);
	}

	100% {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes mobile-card-left {
	0% {
		opacity: 0;
		transform: translateX(42%) scale(0.28) rotate(0deg);
	}

	100% {
		opacity: 1;
		transform: translateX(0) scale(1) rotate(-9deg);
	}
}

@keyframes mobile-card-right {
	0% {
		opacity: 0;
		transform: translateX(-42%) scale(0.28) rotate(0deg);
	}

	100% {
		opacity: 1;
		transform: translateX(0) scale(1) rotate(9deg);
	}
}

@keyframes desktop-card-left {
	0% {
		opacity: 0;
		transform: translateX(-54px) scale(0.82) rotate(-13deg);
	}

	100% {
		opacity: 1;
		transform: translateX(0) scale(1) rotate(-7deg);
	}
}

@keyframes desktop-card-right {
	0% {
		opacity: 0;
		transform: translateX(54px) scale(0.82) rotate(13deg);
	}

	100% {
		opacity: 1;
		transform: translateX(0) scale(1) rotate(7deg);
	}
}

@keyframes category-content-rise {
	0% {
		opacity: 0;
		transform: translateY(14px);
	}

	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes whats-new-pulse {
	0%,
	100% {
		box-shadow:
			0 12px 30px rgba(0, 0, 0, 0.18),
			0 0 22px rgba(218, 146, 11, 0.18);
	}

	50% {
		box-shadow:
			0 12px 30px rgba(0, 0, 0, 0.2),
			0 0 36px rgba(218, 146, 11, 0.34);
	}
}

@keyframes whats-new-dot {
	0%,
	100% {
		opacity: 0.72;
		transform: translateY(-50%) scale(0.92);
	}

	50% {
		opacity: 1;
		transform: translateY(-50%) scale(1.08);
	}
}

@media (min-width: 641px) {
	.viewport-shell {
		padding: 24px 24px calc(16px + env(safe-area-inset-bottom));
	}

	.content-top-wrapper {
		width: min(100%, 920px);
		min-height: 178px;
		margin-bottom: 18px;
	}

	.brand-wordmark {
		display: flex;
	}

	.brand-image {
		width: clamp(122px, 15vw, 170px);
		max-height: 170px;
	}

	.brand-image--left {
		margin-right: 0;
		animation: desktop-card-left 680ms cubic-bezier(0.2, 0.85, 0.2, 1) 760ms
			both;
	}

	.brand-image--right {
		margin-left: 0;
		animation: desktop-card-right 680ms cubic-bezier(0.2, 0.85, 0.2, 1) 900ms
			both;
	}

	.icon-row {
		margin-top: 12px;
		gap: 16px;
		animation-delay: 1120ms;
	}

	.eyebrow {
		animation-delay: 980ms;
	}

	.icon-card {
		padding: 14px 22px;
	}

	.label {
		font-size: 15px;
	}

	.bottom-meta {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		text-align: left;
		gap: 16px;
		padding-top: 16px;
	}

	.bottom-meta__left {
		font-size: 12px;
		white-space: nowrap;
	}

	.bottom-meta__right {
		justify-content: flex-end;

		a {
			font-size: 12px;
		}
	}
}
</style>
